import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Crown, ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { PRODUCTS, COMING_SOON } from "@/data/products";
import { EditorialHeader, MarqueeBand, Reveal, Eyebrow } from "@/components/editorial";
import { MagneticButton } from "@/components/MagneticButton";

export default function ProductsPage() {
  return (
    <div data-testid="products-page">
      <Seo
        title="The Shop"
        description="Digital systems, workbooks, and templates that help elementary school parents stay organized and stress less. Instant access, print + digital."
        path="/products"
      />

      <EditorialHeader
        chapter="Vol. 02"
        eyebrow="The Shop"
        title="Systems built for"
        italicWord="real school life."
        intro="Practical, done-for-you digital products. Tap any one to see what's inside, preview sample pages, and grab instant access."
        testid="products-header"
      />

      <MarqueeBand items={["The Shop", "Print + Digital", "Instant Access", "Lifetime Updates", "Real School Life"]} />

      {/* Editorial product cards — asymmetric grid with staggered offsets */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
              <div>
                <Eyebrow>Chapter 01, the catalog</Eyebrow>
                <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-brand-ink text-4xl md:text-5xl leading-[1.02]">
                  Three <span className="font-editorial italic text-brand-sage">systems.</span> One rhythm.
                </h2>
              </div>
              <p className="hidden md:block text-sm text-brand-inkMuted max-w-xs text-right">
                Start with 01 if picture day snuck up on you. Level up with 02 when you&apos;re ready to run the whole year.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {PRODUCTS.map((p, i) => (
              <Reveal
                key={p.slug}
                delay={i * 0.1}
                className={
                  i === 0
                    ? "md:col-span-7"
                    : i === 1
                    ? "md:col-span-5 md:mt-24"
                    : "md:col-span-8 md:col-start-3 md:-mt-12"
                }
              >
                <div className="group relative block" data-testid={`product-tile-${i}`}>
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`product-tile-link-${p.slug}`}
                    className="absolute inset-0 z-10"
                    aria-label={p.name}
                  />
                  <article className="relative overflow-hidden rounded-[28px] bg-white border border-brand-border">
                    <div className="relative aspect-[16/10] overflow-hidden bg-brand-sage/10">
                      <img
                        src={p.cover}
                        alt={p.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
                      <span
                        className="absolute top-5 left-6 font-editorial italic text-brand-accent text-5xl md:text-6xl leading-none select-none"
                        aria-hidden="true"
                      >
                        0{i + 1}
                      </span>
                      <span className="absolute top-6 right-6 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-bg bg-brand-ink/60 border border-white/20 rounded-full px-3 py-1 backdrop-blur">
                        <Sparkles className="h-3 w-3" /> {p.tagline}
                      </span>
                      <span className="absolute bottom-5 right-6 inline-flex items-center text-brand-bg font-display text-2xl md:text-3xl font-semibold tabular-nums">
                        ${p.price}
                      </span>
                    </div>
                    <div className="p-7 md:p-9">
                      <h3 className="font-display text-2xl md:text-3xl font-medium tracking-[-0.01em] text-brand-ink leading-[1.05]">
                        {p.name}
                      </h3>
                      <p className="mt-4 text-brand-inkSoft leading-relaxed max-w-lg">
                        {p.shortDesc}
                      </p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-sageDark group-hover:text-brand-ink transition-colors">
                        <span className="border-b border-brand-sageDark/50 pb-0.5">See what&apos;s inside</span>
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </article>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon — dark editorial band */}
      <section
        data-testid="coming-soon-section"
        className="relative bg-brand-ink text-brand-bg overflow-hidden py-24 md:py-32"
      >
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
                Chapter 02, the roadmap
              </p>
              <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl sm:text-5xl md:text-6xl leading-[1.02] max-w-2xl">
                What&apos;s <span className="font-editorial italic text-brand-accent">next</span> in the pipeline.
              </h2>
              <p className="mt-5 text-brand-bg/80 max-w-lg">
                Subscribe to the newsletter and you&apos;ll know the moment they launch. No fluff, no filler.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <MagneticButton strength={16}>
                <Link
                  to="/resources#subscribe"
                  data-testid="coming-soon-newsletter-cta"
                  className="inline-flex items-center gap-2 bg-brand-bg text-brand-ink hover:bg-white transition-colors px-6 py-3.5 rounded-full font-medium"
                >
                  Get notified when these launch
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </Reveal>
          </div>

          <div className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {COMING_SOON.map((c, i) => (
              <Reveal
                key={c.name}
                delay={i * 0.08}
                className={c.flagship ? "sm:col-span-2 lg:col-span-2" : ""}
              >
                <div
                  data-testid={`coming-soon-${i}`}
                  className={`relative h-full rounded-2xl border p-6 md:p-8 backdrop-blur-sm transition-colors ${
                    c.flagship
                      ? "border-brand-accent/40 bg-brand-accent/10 hover:bg-brand-accent/15"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-editorial italic text-brand-accent text-3xl leading-none">
                      0{i + 1}
                    </span>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      {c.flagship && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-brand-ink bg-brand-accent px-2.5 py-1 rounded-full">
                          <Crown className="h-3 w-3" /> Flagship
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-brand-bg bg-white/10 border border-white/20 px-2.5 py-1 rounded-full">
                        <Lock className="h-3 w-3" /> Coming soon
                      </span>
                    </div>
                  </div>
                  <h3 className={`mt-6 font-display font-medium leading-[1.15] ${c.flagship ? "text-2xl md:text-3xl" : "text-xl"}`}>
                    {c.name}
                  </h3>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-sage">{c.price}</p>
                  <p className={`mt-4 text-brand-bg/75 leading-relaxed ${c.flagship ? "text-base" : "text-sm"}`}>
                    {c.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
